import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import ActiveOrderPanel, { ActiveOrderPanelHeader } from '../../components/driver/ActiveOrderPanel';
import OrderMap from '../../components/driver/OrderMap';
import OrderSidebar from '../../components/driver/OrderSidebar';
import BottomSheet, { miniVisiblePx } from '../../components/driver/BottomSheet';
import DriverTabBar from '../../components/driver/DriverTabBar';
import MyOrdersSheet from '../../components/driver/MyOrdersSheet';
import CarrierOffersSection from '../../components/driver/CarrierOffersSection';
import LocationToast from '../../components/driver/LocationToast';
import ProfileIncompleteBanner from '../../components/driver/ProfileIncompleteBanner';
import LocationConsentModal from '../../components/driver/LocationConsentModal';
import BroadcastModal from '../../components/common/BroadcastModal';
import { useOpenOrders } from '../../hooks/useOpenOrders';
import { useDriverVehicles } from '../../hooks/useDriverVehicles';
import { driverCanServeOrder } from '../../utils/truckMatching';
import { useDriverLocation } from '../../hooks/useDriverLocation';
import { useDriverActiveOrder } from '../../hooks/useDriverActiveOrder';
import { useDriverBids } from '../../hooks/useDriverBids';
import { useDriverCarrierOffers } from '../../hooks/useDriverCarrierOffers';
import { useAuth } from '../../contexts/AuthContext';
import { useFcmToken } from '../../hooks/useFcmToken';
import { COLORS } from '../../styles/tokens';
import { hasCurrentLocationConsent } from '../../utils/locationConsent';
import NotificationToast from '../../components/NotificationToast';
import { useDriverNotifications } from '../../hooks/useDriverNotifications';

// Reserve før bunnmenyen har målt seg selv (brukes av BottomSheet)
const TAB_BAR_FALLBACK_PX = 74;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}


export default function DriverDashboard() {
  const { userDoc } = useAuth();
  const { orders: openOrders, loading, error } = useOpenOrders(userDoc?.isTestAccount ?? false);
  const vehicles = useDriverVehicles();
  const { location, usingFallback } = useDriverLocation();
  const { activeOrders, loading: activeLoading } = useDriverActiveOrder();
  const isMobile = useIsMobile();
  const driverBids = useDriverBids();
  // Oppdrag som krever en bil sjåføren ikke har, skjules. Oppdrag sjåføren allerede har budt på vises alltid.
  const orders = useMemo(
    () => openOrders.filter(o => driverCanServeOrder(o, vehicles) || driverBids.has(o.id)),
    [openOrders, vehicles, driverBids],
  );
  const { offers: carrierOffers } = useDriverCarrierOffers();
  useFcmToken();
  const { notification, dismiss } = useDriverNotifications();
  const [tabBarPx, setTabBarPx] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedActiveOrder, setSelectedActiveOrder] = useState(null);
  // Fane i mobilarket ligger i URL-en (?tab=mine|new) så bunnmenyen kan styre den.
  // Uten valg: vis «Mine» hvis sjåføren har pågående oppdrag eller forespørsler fra firmaet.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const hasMine = activeOrders.length > 0 || carrierOffers.length > 0;
  const tab = urlTab === 'mine' || urlTab === 'new' ? urlTab : (hasMine ? 'mine' : 'new');
  useEffect(() => {
    if (urlTab === 'mine' || urlTab === 'new' || searchParams.get('active') || activeLoading) return;
    setSearchParams({ tab: hasMine ? 'mine' : 'new' }, { replace: true });
  }, [urlTab, searchParams, activeLoading, hasMine, setSearchParams]);
  const setSheetTab = useCallback(
    v => setSearchParams({ tab: v }, { replace: true }),
    [setSearchParams]
  );
  const [consentDone, setConsentDone]     = useState(false);
  const mapRef = useRef(null);
  const [mapType, setMapType] = useState('roadmap');

  // Valg fra kartet bytter til fanen der kortet ligger, så det faktisk vises i arket
  const handleSelect = useCallback((order) => {
    setSelectedOrder(order);
    setSelectedActiveOrder(null);
    if (order) setSheetTab('new');
  }, [setSheetTab]);

  const handleActiveSelect = useCallback((order) => {
    setSelectedActiveOrder(order);
    setSelectedOrder(null);
    if (order) setSheetTab('mine');
  }, [setSheetTab]);

  // Åpent tildelt oppdrag ligger i URL-en (?active=id), så /driver/active/:id kan sende hit
  // og tilbake-knappen i nettleseren lukker panelet
  const openActiveId = searchParams.get('active');

  const openActive = useCallback((order) => {
    setSelectedActiveOrder(order);
    setSelectedOrder(null);
    setSearchParams({ active: order.id });
  }, [setSearchParams]);

  const closeActive = useCallback(() => {
    setSearchParams({ tab: 'mine' }, { replace: true });
  }, [setSearchParams]);

  // Vis ruten for oppdraget på kartet også når panelet åpnes direkte fra en lenke
  useEffect(() => {
    if (!openActiveId) return;
    const order = activeOrders.find(o => o.id === openActiveId);
    if (order) setSelectedActiveOrder(order);
  }, [openActiveId, activeOrders]);

  const onMapLoad = useCallback(map => { mapRef.current = map; }, []);
  function locateMe() {
    if (!location || !mapRef.current) return;
    mapRef.current.panTo(location);
    mapRef.current.setZoom(14);
  }
  function toggleMapType() {
    const next = mapType === 'roadmap' ? 'satellite' : 'roadmap';
    setMapType(next);
    mapRef.current?.setMapTypeId(next);
  }

  // Åpne oppdrag der sjåføren har lagt bud og venter på svar
  const pendingOrders = orders.filter(o => driverBids.has(o.id));

  const liveSelected = selectedOrder && (orders.find(o => o.id === selectedOrder.id) ?? selectedOrder);

  const showPeek = isMobile && !openActiveId && tab === 'new' && !!liveSelected;
  const sheetInset = tabBarPx || TAB_BAR_FALLBACK_PX;
  const needsConsent = !consentDone && userDoc && !hasCurrentLocationConsent(userDoc);

  return (
    <div className="driver-ui" style={styles.root}>
      <NotificationToast notification={notification} onDismiss={dismiss} />
      <Navbar />
      <ProfileIncompleteBanner />

      {/* Midlertidig melding (6 s) bare når vi faller tilbake til Oslo uten tidligere posisjon */}
      <LocationToast
        active={usingFallback}
        style={isMobile ? styles.locationToastMobile : styles.locationToastDesktop}
      />

      {isMobile ? (
        /* ── MOBIL: kart i bakgrunn, bottom sheet i forgrunnen ── */
        <div style={styles.mobileContainer}>
          <div style={{ position: 'absolute', inset: 0 }}>
            <OrderMap
              fitBottomPx={showPeek ? miniVisiblePx(sheetInset) + 24 : null}
              orders={orders}
              selectedOrder={selectedOrder}
              onSelect={handleSelect}
              driverLocation={location}
              onMapLoad={onMapLoad}
              selectedActiveOrder={selectedActiveOrder}
            />
          </div>
          {!location && <LocatingPill />}
          {location && (
            <button onClick={locateMe} title="Vis min posisjon" style={styles.locateBtn}>
              📍
            </button>
          )}
          <button onClick={toggleMapType} title="Bytt kartvisning" style={styles.layersBtn}>
            <LayersIcon active={mapType === 'satellite'} />
          </button>
          <BottomSheet inset={sheetInset} openKey={selectedOrder?.id ?? null} header={openActiveId
            ? <ActiveOrderPanelHeader onClose={closeActive} />
            : <div style={styles.handle}><span style={styles.handleBar} /></div>}>
            {openActiveId ? (
              <ActiveOrderPanel orderId={openActiveId} onClose={closeActive} />
            ) : tab === 'mine' ? (
              <MyOrdersSheet
                activeOrders={activeOrders}
                pendingOrders={pendingOrders}
                driverBids={driverBids}
                onOpenActive={openActive}
                onOpenPending={handleSelect}
              />
            ) : (
              <OrderSidebar
                orders={orders}
                loading={loading}
                error={error}
                selectedOrder={selectedOrder}
                onSelect={handleSelect}
                driverLocation={location}
                driverBids={driverBids}
                activeOrders={activeOrders}
                selectedActiveOrder={selectedActiveOrder}
                onActiveSelect={handleActiveSelect}
                onOpenActive={openActive}
                tab={tab}
                mobile
              />
            )}
          </BottomSheet>
        </div>
      ) : (
        /* ── DESKTOP: sidebar + kart side om side ── */
        <div style={styles.body}>
          {openActiveId ? (
            <div style={styles.desktopPanel}>
              <div style={{ borderBottom: `1px solid ${COLORS.border}`, background: COLORS.white }}>
                <ActiveOrderPanelHeader onClose={closeActive} />
              </div>
              <ActiveOrderPanel orderId={openActiveId} onClose={closeActive} />
            </div>
          ) : (
            <div style={styles.desktopColumn}>
              <div style={styles.desktopOffers}><CarrierOffersSection /></div>
              <OrderSidebar
                orders={orders}
                loading={loading}
                error={error}
                selectedOrder={selectedOrder}
                onSelect={handleSelect}
                driverLocation={location}
                driverBids={driverBids}
                activeOrders={activeOrders}
                selectedActiveOrder={selectedActiveOrder}
                onActiveSelect={handleActiveSelect}
                onOpenActive={openActive}
              />
            </div>
          )}
          <div style={styles.mapArea}>
            <OrderMap
              orders={orders}
              selectedOrder={selectedOrder}
              onSelect={handleSelect}
              driverLocation={location}
              onMapLoad={onMapLoad}
              selectedActiveOrder={selectedActiveOrder}
            />
            {!location && <LocatingPill />}
            {location && (
              <button onClick={locateMe} title="Vis min posisjon" style={styles.locateBtn}>
                📍
              </button>
            )}
            <button onClick={toggleMapType} title="Bytt kartvisning" style={styles.layersBtn}>
              <LayersIcon active={mapType === 'satellite'} />
            </button>
          </div>
        </div>
      )}

      {needsConsent && (
        <LocationConsentModal onConsent={() => setConsentDone(true)} />
      )}
      <BroadcastModal />
      <DriverTabBar onHeight={setTabBarPx} />
    </div>
  );
}

function LayersIcon({ active }) {
  const c = active ? COLORS.primary : '#555';
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function LocatingPill() {
  return (
    <>
      <style>{`
        @keyframes tippi-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.7); }
        }
      `}</style>
      <div style={{
        position: 'absolute', top: 36, right: 16,
        zIndex: 50, background: 'rgba(255,255,255,0.95)',
        borderRadius: 28, padding: '8px 14px',
        display: 'flex', alignItems: 'center', gap: 8,
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        fontSize: 13, fontWeight: 500, color: '#555',
        whiteSpace: 'nowrap',
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: COLORS.primary, display: 'inline-block', flexShrink: 0,
          animation: 'tippi-pulse 1.2s ease-in-out infinite',
        }} />
        Henter posisjon…
      </div>
    </>
  );
}


const styles = {
  root: {
    display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', position: 'relative',
  },
  mobileContainer: {
    flex: 1, position: 'relative', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  // Posisjonsmeldingen flyter over kartet så innholdet ikke flyttes nedover
  locationToastMobile: { top: 96, left: 16, right: 76 },
  locationToastDesktop: { top: 64, left: '50%', transform: 'translateX(-50%)', maxWidth: 'calc(100vw - 32px)' },
  body: {
    flex: 1, display: 'flex', overflow: 'hidden',
  },
  desktopPanel: {
    width: 380, display: 'flex', flexDirection: 'column',
    borderRight: `1px solid ${COLORS.border}`, background: COLORS.bg, overflow: 'hidden',
  },
  // Forespørsler fra firmaet over sidebaren, i samme kolonnebredde som sidebaren
  desktopColumn: { width: 320, display: 'flex', flexDirection: 'column', minHeight: 0, background: COLORS.bg },
  desktopOffers: { padding: '0 16px', boxSizing: 'border-box', overflowY: 'auto', maxHeight: '40%', flexShrink: 0 },
  mapArea: {
    flex: 1, position: 'relative', display: 'flex', flexDirection: 'column',
  },
  handle: {
    display: 'flex', justifyContent: 'center', padding: '10px 0 8px',
  },
  handleBar: {
    width: 40, height: 4, borderRadius: 2, background: COLORS.border,
  },
  locateBtn: {
    position: 'absolute', top: 36, right: 16, zIndex: 50,
    width: 44, height: 44, borderRadius: '50%',
    border: 'none', background: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
    cursor: 'pointer', fontSize: 20,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  layersBtn: {
    position: 'absolute', top: 88, right: 16, zIndex: 50,
    width: 44, height: 44, borderRadius: '50%',
    border: 'none', background: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
